import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import {
    IconDollarCircleArrowsNoShadow,
    IconDotInCircle,
    IconDownChevron,
    IconPlusInBox
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import AddPromoCampaign from 'components/UI/Modal/AddPromoCampaign/AddPromoCampaign'
import StackedAvatar from 'components/UI/StackedAvatar/StackedAvatar'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deletePromoCampaign,
    getPromoCampaigns,
    updatePromoCampaign
} from 'services/endpoints/settings/general/subscription'
import { type PromoCampaign } from 'types/api/subscription'

import style from './_promo-campaigns.module.scss'
import { ClaimedUsersModal } from '../components/ClaimedUsersModal'
import { SubscriptionCard } from '../components/SubscriptionCard'

export const PromoCampaigns = () => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const modalData = useModalContext()

    const [activeFilter, setActiveFilter] = useState('Active')
    const [openCampaignOptions, setOpenCampaignOptions] = useState<
        Record<number, boolean>
    >({})

    const { data, isLoading } = useQuery(
        'promoCampaigns',
        async () => await getPromoCampaigns('promo')
    )

    const dateArray = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ]

    const options = [
        {
            value: 'new',
            label: 'New Subscribers'
        },
        {
            value: 'returning',
            label: 'Returning Subscribers'
        },
        {
            value: 'expired',
            label: 'Returning Subscribers'
        },
        {
            value: 'all',
            label: 'New & Returning Subscribers'
        },
        {
            value: 'both',
            label: 'New & Returning Subscribers'
        }
    ]

    const filterTabs = ['Active', 'Disabled', 'Expired']

    const toggleCampaignMutation = useMutation(
        async (data: {
            id: number
            index: number
            campaignData: PromoCampaign
        }) => await updatePromoCampaign(data.id, data.campaignData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('promoCampaigns')
            },
            onError: () => {
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    const deleteCampaignMutation = useMutation(
        async (id: number) => await deletePromoCampaign(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('promoCampaigns')
            },
            onError: () => {
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    title='Promotional Campaigns'
                    section={t('subscriptions')}
                />
            }
        >
            <div className={style.filter_tabs}>
                {filterTabs.map((tab: string) => (
                    <div
                        key={tab}
                        className={`${style.filter_tab} ${
                            activeFilter === tab ? style.filter_tab_active : ''
                        }`}
                        onClick={() => {
                            setActiveFilter(tab)
                        }}
                    >
                        {tab}
                    </div>
                ))}
            </div>

            {isLoading && <div className='loader'></div>}

            {!isLoading &&
                data
                    ?.filter((campaign: any) => {
                        const today = new Date()

                        if (activeFilter === 'Active') {
                            return (
                                campaign.active === 1 &&
                                today.getTime() <
                                    new Date(campaign.expiry_at).getTime()
                            )
                        }

                        if (activeFilter === 'Disabled') {
                            return campaign.active === 0
                        }

                        if (activeFilter === 'Expired') {
                            return (
                                new Date(campaign.expiry_at).getTime() <=
                                today.getTime()
                            )
                        }

                        return false
                    })
                    .map((campaign: any) => {
                        const createdDate = new Date(campaign.created_at)
                        const expireDate = new Date(campaign.expiry_at)

                        const audienceLabel =
                            options.find(
                                option => option.value === campaign.audience
                            )?.label || campaign.audience

                        const optionsOpen = !!openCampaignOptions[campaign.id]

                        const title = `Free Trial - ${audienceLabel}`

                        return (
                            <SubscriptionCard
                                type='card'
                                customClass={style.promo_campaign_card}
                                key={campaign.id}
                                icon={<IconDollarCircleArrowsNoShadow />}
                                alwaysExpanded={true}
                                selectable={false}
                                hideArrow={true}
                                isActive={campaign.active === 1}
                                switchFn={() => {
                                    toggleCampaignMutation.mutate({
                                        id: campaign.id,
                                        index: data.indexOf(campaign),
                                        campaignData: {
                                            type: campaign.type,
                                            audience: campaign.audience,
                                            discount: campaign.discount,
                                            limit: campaign.limit,
                                            length: campaign.length,
                                            active: !campaign.active
                                        }
                                    })
                                }}
                                editFn={() => {
                                    modalData.addModal(
                                        'Update Promotional Campaign',
                                        <AddPromoCampaign
                                            isEdit={true}
                                            campaign={campaign}
                                        />
                                    )
                                }}
                                deleteFn={
                                    optionsOpen && activeFilter !== 'Expired'
                                        ? () => {
                                              deleteCampaignMutation.mutate(
                                                  campaign.id
                                              )
                                          }
                                        : undefined
                                }
                                hideEdit={
                                    activeFilter === 'Expired' || !optionsOpen
                                }
                                hideToggle={
                                    activeFilter === 'Expired' || !optionsOpen
                                }
                                name={
                                    <div className={style.campaign_header}>
                                        <div
                                            className={
                                                style.campaign_header_text
                                            }
                                        >
                                            <div className={style.card_title}>
                                                {audienceLabel}
                                            </div>

                                            <div className={style.duration}>
                                                {campaign.free === 1
                                                    ? title
                                                    : `${campaign.discount}% Off for ${campaign.length} Days`}
                                            </div>
                                        </div>

                                        <button
                                            type='button'
                                            className={`${
                                                style.campaign_options_button
                                            } ${
                                                optionsOpen
                                                    ? style.campaign_options_button_open
                                                    : ''
                                            }`}
                                            onClick={event => {
                                                event.stopPropagation()

                                                setOpenCampaignOptions(prev => ({
                                                    ...prev,
                                                    [campaign.id]:
                                                        !prev[campaign.id]
                                                }))
                                            }}
                                        >
                                            <IconDownChevron />
                                        </button>
                                    </div>
                                }
                                descritpion={
                                    <div
                                        className={style.description_container}
                                    >
                                        <div
                                            className={
                                                style.description_bottom_area
                                            }
                                        >
                                            <div
                                                className={
                                                    style.description_title
                                                }
                                            >
                                                Details
                                            </div>

                                            <div
                                                className={
                                                    style.description_date_container
                                                }
                                            >
                                                <div
                                                    className={
                                                        style.description_date
                                                    }
                                                >
                                                    Created{' '}
                                                    <span>
                                                        {
                                                            dateArray[
                                                                createdDate.getMonth()
                                                            ]
                                                        }{' '}
                                                        {createdDate.getDate()}{' '}
                                                        {createdDate.getFullYear()}
                                                    </span>
                                                </div>

                                                <div
                                                    className={
                                                        style.description_date
                                                    }
                                                >
                                                    Expires{' '}
                                                    <span>
                                                        {
                                                            dateArray[
                                                                expireDate.getMonth()
                                                            ]
                                                        }{' '}
                                                        {expireDate.getDate()}{' '}
                                                        {expireDate.getFullYear()}
                                                    </span>
                                                </div>
                                            </div>

                                            <div
                                                className={
                                                    style.description_limit
                                                }
                                            >
                                                <IconDotInCircle />
                                                <span>
                                                    {Number(campaign.limit) === 0
                                                        ? 'Unlimited'
                                                        : `${campaign.limit} Subscriptions Max`}
                                                </span>
                                            </div>

                                            <div
                                                className={
                                                    style.description_options
                                                }
                                            >
                                                <div
                                                    className={
                                                        style.description_claimed_by
                                                    }
                                                    style={{
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        modalData.addModal(
                                                            'Trial Claimed By',
                                                            <ClaimedUsersModal
                                                                users={
                                                                    campaign.users
                                                                }
                                                            />
                                                        )
                                                    }}
                                                >
                                                    <div
                                                        className={
                                                            style.description_claimed_by_text
                                                        }
                                                    >
                                                        Claimed by
                                                    </div>{' '}
                                                    {campaign.used > 0 ? (
                                                        <div
                                                            className={
                                                                style.stacked_img_container
                                                            }
                                                        >
                                                            <StackedAvatar
                                                                avatars={
                                                                    campaign.avatars
                                                                }
                                                                count={
                                                                    campaign.used
                                                                }
                                                                avtarsCount={5}
                                                                customClass={
                                                                    style.stacked_img
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span>
                                                            {campaign.used}{' '}
                                                            Users
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                        )
                    })}

            {!isLoading && (
                <ActionCard
                    icon={<IconPlusInBox width='22' height='22' />}
                    text={'Add New Campaign'}
                    addNewText
                    clickFn={() => {
                        modalData.addModal(
                            'Promotional Campaign',
                            <AddPromoCampaign />
                        )
                    }}
                />
            )}
        </WithHeaderSection>
    )
}